import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	IconButton,
	Card,
	// CardContent,
	Typography,
	AppBar,
	Toolbar,
	Avatar,
	Box,
	CircularProgress,
	Container,
	Drawer,
	Grid2 as Grid, // <-- add this import
	Button,
	Alert,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { setUser } from "../features/UserSlice";
import logo from "../Malayan.png";
import { sideMenuList, toggleDrawer } from "../utility/utils";
import ReportDataGrid from "./components/ReportDataGrid";
import SimpleBarChart from "./components/SimpleBarChart";
import { getAnalytics } from "../utility/api";
import { exportToCSV } from "../utility/utils";

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];
const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));
const CURRENT_MONTH = new Date().getMonth() + 1;
const CURRENT_YEAR = new Date().getFullYear();

// Fixed status order & colors for the MTD bar chart
const STATUS_BUCKETS = [
	{ label: "NEW", color: "#4472C4", match: ["new", "created"] },
	{ label: "ASSIGNED", color: "#FFC000", match: ["assigned"] },
	{ label: "IN PROGRESS", color: "#F4B183", match: ["inprogress"] },
	{ label: "REVIEW", color: "#9DC3E6", match: ["review", "inreview"] },
	{ label: "APPROVED", color: "#A9D18E", match: ["approved"] },
	{ label: "COMPLETED", color: "#00B050", match: ["completed"] },
];

const reportsList = [
	{
		name: "MTD",
		label: "Assignment MTD Status Report",
		width: 6,
		height: 500,
		type: "statusChart",
		exportType: "SRE",
		monthFilter: true,
	},
	{
		name: "MCC",
		label: "Monthly Created Completed Report",
		width: 6,
		height: 500,
		type: "mccChart",
		exportType: "SRE",
	},
	{
		// Aging reports always cover every active (non-completed) assignment,
		// so they carry no month filter.
		name: "ACD",
		label: "Assignment Aging Calendar Days Report",
		width: 12,
		height: 500,
		type: "agingGrid",
		exportType: "AGE",
	},
	{
		name: "AWD",
		label: "Assignment Aging Working Days Report",
		width: 12,
		height: 500,
		type: "agingGrid",
		exportType: "AGE",
	},
	{
		name: "FAAF",
		label: "Final Adjusted Amount Finance Report",
		width: 6,
		height: 500,
		type: "grid",
		exportType: "FRE",
		invoiceMonthFilter: true,
	},
	{
		name: "FIA",
		label: "Finance Report Invoice Amount Report",
		width: 6,
		height: 500,
		type: "grid",
		exportType: "FRE",
		invoiceMonthFilter: true,
	},
];

// SQL Server preserves the column case declared in the views; read case-insensitively
const getField = (row, name) => {
	const key = Object.keys(row).find(
		(k) => k.toLowerCase() === name.toLowerCase()
	);
	return key !== undefined ? row[key] : undefined;
};

const normalizeStatus = (status) =>
	String(status || "")
		.toLowerCase()
		.replace(/[^a-z]/g, "");

// Rows of Vw_Assignment_Monthly_Status_Report -> one bar per status
const buildStatusChartData = (rows) => {
	const chartData = STATUS_BUCKETS.map((bucket) => ({
		label: bucket.label,
		count: rows
			.filter((row) =>
				bucket.match.includes(normalizeStatus(getField(row, "STATUS")))
			)
			.reduce(
				(sum, row) => sum + (Number(getField(row, "COUNT")) || 0),
				0
			),
	}));
	// keep statuses we don't recognise visible instead of dropping them
	const known = STATUS_BUCKETS.flatMap((bucket) => bucket.match);
	rows.forEach((row) => {
		const status = normalizeStatus(getField(row, "STATUS"));
		if (status && !known.includes(status)) {
			chartData.push({
				label: String(getField(row, "STATUS")).toUpperCase(),
				count: Number(getField(row, "COUNT")) || 0,
			});
		}
	});
	return chartData;
};

const STATUS_COLOR_BY_LABEL = STATUS_BUCKETS.reduce((acc, bucket) => {
	acc[bucket.label] = bucket.color;
	return acc;
}, {});

// Rows of Vw_Assignment_Monthly_Created_Completed_Report -> full year Jan..Dec
const buildMccChartData = (rows) =>
	MONTH_SHORT.map((label, index) => {
		const monthRows = rows.filter(
			(row) => Number(getField(row, "MONTH_NUMBER")) === index + 1
		);
		return {
			label,
			created: monthRows.reduce(
				(sum, row) => sum + (Number(getField(row, "CREATED")) || 0),
				0
			),
			completed: monthRows.reduce(
				(sum, row) => sum + (Number(getField(row, "COMPLETED")) || 0),
				0
			),
		};
	});

// Aging views expose one row per company + creation month; roll them up into a
// single row per company so the grid shows every active assignment. Completed
// assignments are already excluded by the views.
const AGING_HELPER_COLUMNS = ["month_number", "month", "year_number"];
const aggregateAgingRows = (rows) => {
	const byCompany = new Map();
	rows.forEach((row) => {
		const company = getField(row, "INSURANCECOMPANY");
		const target = byCompany.get(company) || {
			INSURANCECOMPANY: company,
		};
		Object.entries(row).forEach(([key, value]) => {
			const lowerKey = key.toLowerCase();
			if (
				lowerKey === "insurancecompany" ||
				AGING_HELPER_COLUMNS.includes(lowerKey)
			) {
				return;
			}
			target[key] = (Number(target[key]) || 0) + (Number(value) || 0);
		});
		byCompany.set(company, target);
	});
	return Array.from(byCompany.values());
};

// The finance views carry the invoice period in a "YYYY-MM" column. The month
// drop-down is built from the periods actually present in the data and the
// filter runs client-side, so no extra round-trip is needed.
// tolerates both "2025-06" and a full date such as "2025-06-01T00:00:00"
const INVOICE_MONTH_PATTERN = /^(\d{4})-(\d{2})/;

const parseInvoicePeriod = (value) => {
	const match = INVOICE_MONTH_PATTERN.exec(String(value ?? ""));
	return match ? `${match[1]}-${match[2]}` : null;
};

const getInvoiceMonthKey = (rows) => {
	for (const row of rows) {
		const key = Object.keys(row).find(
			(k) =>
				k.toLowerCase().includes("month") &&
				parseInvoicePeriod(row[k]) !== null
		);
		if (key) return key;
	}
	return null;
};

const formatInvoiceMonth = (period) => {
	const [year, month] = period.split("-");
	return `${MONTH_NAMES[Number(month) - 1] ?? month} ${year}`;
};

// Most recent period first
const buildInvoiceMonthOptions = (rows) => {
	const key = getInvoiceMonthKey(rows);
	if (!key) return [];
	const periods = new Set(
		rows.map((row) => parseInvoicePeriod(row[key])).filter(Boolean)
	);
	return Array.from(periods)
		.sort()
		.reverse()
		.map((period) => ({ value: period, label: formatInvoiceMonth(period) }));
};

const filterByInvoiceMonth = (rows, period) => {
	if (!period) return rows;
	const key = getInvoiceMonthKey(rows);
	if (!key) return rows;
	return rows.filter((row) => parseInvoicePeriod(row[key]) === period);
};

// The finance amounts are stored as SQL FLOAT, so a value of 2018.3 comes back
// as 2018.300048828125. Amounts are reported in whole ringgit - round them and
// drop the decimals everywhere they are shown or exported.
const AMOUNT_COLUMN_PATTERN = /amount/i;
const NON_AMOUNT_COLUMNS = ["id", "month_number", "year_number"];

const isNumericValue = (value) =>
	typeof value === "number"
		? Number.isFinite(value)
		: typeof value === "string" &&
		  value.trim() !== "" &&
		  Number.isFinite(Number(value));

// An amount column is numeric throughout and is either named like an amount or
// carries decimals; counts and year / month helper columns stay untouched.
const isAmountColumn = (rows, key) => {
	if (NON_AMOUNT_COLUMNS.includes(key.toLowerCase())) return false;
	const values = rows
		.map((row) => row[key])
		.filter((value) => value !== null && value !== undefined && value !== "");
	if (values.length === 0 || !values.every(isNumericValue)) return false;
	return (
		AMOUNT_COLUMN_PATTERN.test(key) ||
		values.some((value) => !Number.isInteger(Number(value)))
	);
};

const roundAmount = (value) => {
	if (value === null || value === undefined || value === "") return value;
	return isNumericValue(value) ? Math.round(Number(value)) : value;
};

const formatAmount = (value) => {
	const rounded = roundAmount(value);
	return typeof rounded === "number"
		? rounded.toLocaleString("en-US")
		: rounded;
};

// Rounds the amount columns of the rows handed to the CSV export so the file
// matches what the grid shows.
const roundAmountColumns = (rows) => {
	if (!Array.isArray(rows) || rows.length === 0) return rows;
	const amountKeys = Object.keys(rows[0]).filter((key) =>
		isAmountColumn(rows, key)
	);
	if (amountKeys.length === 0) return rows;
	return rows.map((row) => {
		const rounded = { ...row };
		amountKeys.forEach((key) => {
			rounded[key] = roundAmount(rounded[key]);
		});
		return rounded;
	});
};

const generateColumnsFromData = (data) => {
	if (!Array.isArray(data) || data.length === 0) return [];

	const firstRow = data[0];
	return Object.keys(firstRow).map((key) => {
		if (key === "id") {
			return {
				field: "id",
				headerName: "ID",
				width: 20,
				sortable: false,
				filterable: false,
			};
		}
		if (key === "INSURANCECOMPANY") {
			return {
				field: "INSURANCECOMPANY",
				headerName: "INSURANCECOMPANY",
				width: 300,
				sortable: false,
				filterable: false,
			};
		}
		return {
			field: key,
			headerName: key
				.replace(/_/g, " ")
				.replace(/\b\w/g, (l) => l.toUpperCase()),
			flex: 1,
			...(isAmountColumn(data, key)
				? { valueFormatter: (value) => formatAmount(value) }
				: {}),
		};
	});
};

export default function Reports(props) {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const user = useSelector((state) => state.user.user);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [reportStates, setReportStates] = useState(
		reportsList.reduce((acc, report) => {
			acc[report.name] = {
				loading: false,
				error: "",
				data: [],
			};
			return acc;
		}, {})
	);
	// MTD is filtered server-side and defaults to the current month
	const [monthSelections, setMonthSelections] = useState({
		MTD: CURRENT_MONTH,
	});
	// Finance reports filter client-side on the invoice period; "" = All months
	const [invoiceMonthSelections, setInvoiceMonthSelections] = useState({
		FAAF: "",
		FIA: "",
	});

	const fetchReport = useCallback((report, month) => {
		setReportStates((prev) => ({
			...prev,
			[report.name]: {
				...prev[report.name],
				loading: true,
				error: "",
			},
		}));
		const params =
			report.name === "MTD"
				? { month, year: CURRENT_YEAR }
				: report.name === "MCC"
				? { year: CURRENT_YEAR }
				: {};
		getAnalytics(report.name, params)
			.then((data) => {
				setReportStates((prev) => ({
					...prev,
					[report.name]: {
						...prev[report.name],
						loading: false,
						data: Array.isArray(data) ? data : [],
						error: "",
					},
				}));
			})
			.catch(() => {
				setReportStates((prev) => ({
					...prev,
					[report.name]: {
						...prev[report.name],
						loading: false,
						error: "Failed to load report data.",
						data: [],
					},
				}));
			});
	}, []);

	useEffect(() => {
		reportsList.forEach((report) => {
			fetchReport(
				report,
				report.name === "MTD" ? CURRENT_MONTH : undefined
			);
		});
	}, [fetchReport]);

	const handleMonthChange = (report, month) => {
		setMonthSelections((prev) => ({ ...prev, [report.name]: month }));
		fetchReport(report, month);
	};

	const handleInvoiceMonthChange = (report, period) => {
		setInvoiceMonthSelections((prev) => ({
			...prev,
			[report.name]: period,
		}));
	};

	// Export exactly what the user currently sees / has selected. Table
	// reports export the displayed rows; the chart reports export the raw
	// status rows (SRE) for the same period, which carry the STATUS column.
	const handleExport = async (report) => {
		const month = monthSelections[report.name];
		const period = invoiceMonthSelections[report.name];
		try {
			let rows;
			let fileName = `${report.name}_Export`;
			if (report.type === "agingGrid" || report.type === "grid") {
				const state = reportStates[report.name];
				const displayed =
					report.type === "agingGrid"
						? aggregateAgingRows(state?.data || [])
						: filterByInvoiceMonth(state?.data || [], period);
				rows = displayed.map((row, index) => ({
					ID: index + 1,
					...row,
				}));
				if (report.invoiceMonthFilter) {
					fileName = `${report.name}_Export_${
						period ? period.replace("-", "_") : "All_Months"
					}`;
				}
			} else {
				const params =
					report.name === "MTD"
						? { month, year: CURRENT_YEAR }
						: { year: CURRENT_YEAR };
				rows = await getAnalytics("SRE", params);
				fileName =
					report.name === "MTD"
						? `${report.name}_Export_${
								MONTH_NAMES[month - 1]
						  }_${CURRENT_YEAR}`
						: `${report.name}_Export_${CURRENT_YEAR}`;
			}
			if (!Array.isArray(rows) || rows.length === 0) {
				alert("No data to export for the current selection.");
				return;
			}
			exportToCSV(roundAmountColumns(rows), fileName);
		} catch (err) {
			alert("Export failed.");
		}
	};

	const handleMenuItemClick = (item) => {
		switch (item) {
			case "My Assignments":
			case "All Assignments":
				navigate("/home");
				break;
			case "New Assignment":
				navigate("/claim/New");
				break;
			case "Data Reports":
				navigate("/data-reports");
				break;
			case "Create User":
				navigate("/createUser");
				break;
			case "Change Password":
				navigate("/changePassword");
				break;
			case "Profile":
				navigate("/profile");
				break;
			case "Logout":
				handleLogout();
				break;
			default:
				break;
		}
	};

	const handleLogout = () => {
		dispatch(setUser(null));
		navigate("/");
	};

	const renderMonthSelect = (report) => (
		<FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
			<InputLabel id={`${report.name}-month-label`}>Month</InputLabel>
			<Select
				labelId={`${report.name}-month-label`}
				label="Month"
				value={monthSelections[report.name] ?? CURRENT_MONTH}
				onChange={(e) =>
					handleMonthChange(report, Number(e.target.value))
				}
			>
				{MONTH_NAMES.map((monthName, index) => (
					<MenuItem key={monthName} value={index + 1}>
						{monthName}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);

	const renderInvoiceMonthSelect = (report, options) => (
		<FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
			<InputLabel id={`${report.name}-invoice-month-label`}>
				Month
			</InputLabel>
			<Select
				labelId={`${report.name}-invoice-month-label`}
				label="Month"
				value={invoiceMonthSelections[report.name] ?? ""}
				onChange={(e) =>
					handleInvoiceMonthChange(report, e.target.value)
				}
			>
				<MenuItem value="">All Months</MenuItem>
				{options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);

	const renderReportContent = (report) => {
		const state = reportStates[report.name];
		if (state?.loading) {
			return <CircularProgress />;
		}
		if (state?.error) {
			return <Alert severity="error">{state.error}</Alert>;
		}

		if (report.type === "statusChart") {
			return (
				<SimpleBarChart
					data={buildStatusChartData(state.data)}
					series={[
						{
							key: "count",
							label: "Assignments",
							colorByLabel: STATUS_COLOR_BY_LABEL,
						},
					]}
					height={report.height - 150}
					showValueLabels
				/>
			);
		}

		if (report.type === "mccChart") {
			return (
				<SimpleBarChart
					data={buildMccChartData(state.data)}
					series={[
						{ key: "created", label: "Created", color: "#FFC000" },
						{
							key: "completed",
							label: "Completed",
							color: "#548235",
						},
					]}
					height={report.height - 150}
					showLegend
					showValueLabels
				/>
			);
		}

		const rows =
			report.type === "agingGrid"
				? aggregateAgingRows(state.data)
				: filterByInvoiceMonth(
						state.data,
						invoiceMonthSelections[report.name]
				  );
		const processedRows = rows.map((row, index) => ({
			id: index + 1,
			...row,
		}));
		return (
			<ReportDataGrid
				reportData={rows}
				exportData={rows}
				columns={generateColumnsFromData(processedRows)}
				reportName={report.name}
			/>
		);
	};

	return (
		<Box
			sx={{
				width: "100%",
				margin: "auto",
				paddingBottom: 3,
			}}
		>
			<AppBar position="static">
				<Toolbar>
					<IconButton
						edge="start"
						color="inherit"
						aria-label="menu"
						onClick={toggleDrawer(setDrawerOpen)(true)}
					>
						<MenuIcon />
					</IconButton>
					<Box
						component="img"
						src={logo}
						alt="Logo"
						sx={{ height: 40, mr: 2, borderRadius: "50%" }}
					/>
					<Typography variant="h6" sx={{ flexGrow: 1 }}>
						Reports
					</Typography>
					<IconButton edge="end" color="inherit">
						<Avatar>
							<AccountCircle />
						</Avatar>
					</IconButton>
				</Toolbar>
			</AppBar>
			<Drawer
				anchor="left"
				open={drawerOpen}
				onClose={toggleDrawer(setDrawerOpen)(false)}
			>
				{sideMenuList(
					toggleDrawer(setDrawerOpen),
					handleMenuItemClick,
					user?.role
				)}
			</Drawer>
			<Container
				maxWidth="xl"
				sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}
			>
				<Grid container spacing={{ xs: 2, md: 3 }}>
					{reportsList.map((report) => (
						<Grid
							item
							size={{ xs: 12, md: report.width }}
							key={report.name}
						>
							<Card
								sx={{
									minHeight: { xs: 350, md: report.height },
									display: "flex",
									flexDirection: "column",
									p: { xs: 1.5, md: 2 },
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
										flexWrap: "wrap",
									}}
								>
									<Button
										variant="outlined"
										sx={{ mb: 2 }}
										onClick={() => handleExport(report)}
									>
										Export
									</Button>
									{report.monthFilter &&
										renderMonthSelect(report)}
									{report.invoiceMonthFilter &&
										renderInvoiceMonthSelect(
											report,
											buildInvoiceMonthOptions(
												reportStates[report.name]
													?.data || []
											)
										)}
								</Box>
								<Typography
									variant="h6"
									gutterBottom
									sx={{
										fontSize: {
											xs: "0.95rem",
											md: "1.25rem",
										},
									}}
								>
									{report.label}
								</Typography>
								{renderReportContent(report)}
							</Card>
						</Grid>
					))}
				</Grid>
			</Container>
		</Box>
	);
}
